import { PackageVariant } from "@carfax/web-ui/components/PricingCard/components/IconPackage/IconPackage.types";

export enum B2CPackages {
  single = "single",
  medium = "medium",
  large = "large",
}

export enum B2BPackage {
  b2bSmall = "b2bSmall",
  b2bMedium = "b2bMedium",
  b2bLarge = "b2bLarge",
  b2bNetSmall = "b2bNetSmall",
  b2bNetMedium = "b2bNetMedium",
  b2bNetLarge = "b2bNetLarge",
}

export enum PartnerPackages {
  SUBITO = "subito",
}

export enum PromotionPackages {
  TWO_CREDITS = "twoCredits",
}

export enum PackagesNameTracking {
  Single = "starter",
  Advanced = "advanced",
  Professional = "professional",
  B2BSmall = "b2bSmall",
  B2BMedium = "b2bMedium",
  B2BLarge = "b2bLarge",
}

export type ProductNameTracking = PackagesNameTracking | PromotionPackages | PartnerPackages;

const isOwnKey = <T extends object>(table: T, key: string): key is Extract<keyof T, string> =>
  Object.prototype.hasOwnProperty.call(table, key);

const isEnumValue = <E extends Record<string, string>>(enumObject: E, value: string): value is E[keyof E] =>
  Object.values(enumObject).some((enumValue) => enumValue === value);

const packageVariantByName = {
  single: PackageVariant.Single,
  medium: PackageVariant.Advanced,
  large: PackageVariant.Professional,
  b2bSmall: PackageVariant.B2BSmall,
  b2bMedium: PackageVariant.B2BMedium,
  b2bLarge: PackageVariant.B2BLarge,
  b2bGo: PackageVariant.B2BNetSmall,
  b2bPro: PackageVariant.B2BNetMedium,
  b2bElite: PackageVariant.B2BNetLarge,
} satisfies Record<string, PackageVariant>;

const trackingNameByPackageName = {
  single: PackagesNameTracking.Single,
  medium: PackagesNameTracking.Advanced,
  large: PackagesNameTracking.Professional,
  b2bSmall: PackagesNameTracking.B2BSmall,
  b2bMedium: PackagesNameTracking.B2BMedium,
  b2bLarge: PackagesNameTracking.B2BLarge,
  b2bNetSmall: PackageVariant.B2BSmall,
  b2bNetMedium: PackageVariant.B2BMedium,
  b2bNetLarge: PackageVariant.B2BLarge,
} satisfies Record<string, PackagesNameTracking | PackageVariant>;

const resolvePackageName = (packageName: string) =>
  isOwnKey(packageVariantByName, packageName) ? packageVariantByName[packageName] : packageName;

const resolvePackageVariant = (packageName: string) => {
  const resolved = resolvePackageName(packageName);
  return isEnumValue(PackageVariant, resolved) ? resolved : PackageVariant.Single;
};

const resolvePackageNameForTracking = (packageName: string) =>
  isOwnKey(trackingNameByPackageName, packageName) ? trackingNameByPackageName[packageName] : packageName;

const bestsellersId: string[] = [B2CPackages.medium, B2BPackage.b2bLarge];

const isB2BPackage = (packageName: string | null | undefined): packageName is B2BPackage =>
  !!packageName && isEnumValue(B2BPackage, packageName);

const isB2CPackage = (packageName: string | null | undefined): packageName is B2CPackages =>
  !!packageName && isEnumValue(B2CPackages, packageName);

export const packagesNaming = {
  resolvePackageName,
  resolvePackageVariant,
  resolvePackageNameForTracking,
  bestsellersId,
  isB2BPackage,
  isB2CPackage,
};
